import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

function getProviderAssetIdKey(providerAssetId) {
  if (!providerAssetId) return '';
  if (typeof providerAssetId === 'object') return stableStringify(providerAssetId);
  return String(providerAssetId);
}

export function toNumericDate(date) {
  if (typeof date === 'number' && Number.isFinite(date)) return date;
  if (typeof date === 'string' && /^\d+$/.test(date)) return Number(date);
  return null;
}

export function normalizeDates(dates = []) {
  return [...new Set((dates || []).map((date) => toNumericDate(date)).filter((date) => date !== null))].sort((a, b) => a - b);
}

export function generateMonthStartDates(start, end) {
  if (!Number.isFinite(start) || !Number.isFinite(end) || start > end) return [];

  const output = [];
  let cursor = dayjs.utc(start).startOf('month').startOf('day').valueOf();
  const limit = dayjs.utc(end).startOf('month').startOf('day').valueOf();

  while (cursor <= limit) {
    output.push(cursor);
    cursor = dayjs.utc(cursor).add(1, 'month').startOf('month').startOf('day')
      .valueOf();
  }

  return output;
}

export function toApiAsset(asset = {}) {
  return {
    ...asset,
    providerAssetId: asset?.providerAssetId || null,
    currency: asset?.currency || asset?.transactionCurrency || asset?.assetCurrency || asset?.accountCurrency,
  };
}

export function assetScopeKey(asset = {}) {
  const normalized = toApiAsset(asset || {});
  const currency = normalized?.currency || '';
  return `${normalized?.assetId || ''}|${getProviderAssetIdKey(normalized?.providerAssetId)}|${currency}`;
}

export function dedupeAssets(assets = []) {
  const seen = new Set();
  const output = [];

  (assets || []).forEach((asset) => {
    const normalized = toApiAsset(asset || {});
    if (!normalized?.assetId) return;
    const key = assetScopeKey(normalized);
    if (seen.has(key)) return;
    seen.add(key);
    output.push(normalized);
  });

  return output;
}

export function normalizeScope(scope = {}) {
  const asset = toApiAsset(scope?.asset || {});
  if (!asset?.assetId) return null;

  const requiredDates = normalizeDates(scope?.requiredDates || []);
  const fallbackDate = requiredDates.length > 0 ? Math.max(...requiredDates) : dayjs.utc().startOf('month').startOf('day').valueOf();
  const rawFromDate = toNumericDate(scope?.fromDate);
  const rawToDate = toNumericDate(scope?.toDate);
  const fromDate = rawFromDate === null ? fallbackDate : rawFromDate;
  const toDate = rawToDate === null ? Math.max(fromDate, fallbackDate) : rawToDate;
  const normalizedFrom = Math.min(fromDate, toDate);
  const normalizedTo = Math.max(fromDate, toDate);
  const normalizedRequiredDates = requiredDates.length > 0 ? requiredDates : [normalizedTo];

  return {
    asset,
    fromDate: normalizedFrom,
    toDate: normalizedTo,
    requiredDates: normalizedRequiredDates,
    includeManualQuotes: scope?.includeManualQuotes ?? true,
  };
}

export function normalizeScopes(scopes = []) {
  const byKey = {};

  (scopes || []).forEach((scope) => {
    const normalizedScope = normalizeScope(scope);
    if (!normalizedScope) return;
    const key = assetScopeKey(normalizedScope.asset);
    const existing = byKey[key];
    if (!existing) {
      byKey[key] = normalizedScope;
      return;
    }

    byKey[key] = {
      ...existing,
      fromDate: Math.min(existing.fromDate, normalizedScope.fromDate),
      toDate: Math.max(existing.toDate, normalizedScope.toDate),
      requiredDates: normalizeDates((existing.requiredDates || []).concat(normalizedScope.requiredDates || [])),
      includeManualQuotes: Boolean(existing.includeManualQuotes || normalizedScope.includeManualQuotes),
    };
  });

  return Object.keys(byKey).sort().map((key) => byKey[key]);
}

export function getDatesFromScopes(scopes = []) {
  const dates = [];
  normalizeScopes(scopes).forEach((scope) => {
    dates.push(scope.fromDate, scope.toDate);
    dates.push(...normalizeDates(scope?.requiredDates || []));
  });
  return normalizeDates(dates);
}

export function getExpandedDatesFromScopes(scopes = []) {
  const dates = [];
  normalizeScopes(scopes).forEach((scope) => {
    const fromDate = toNumericDate(scope?.fromDate);
    const toDate = toNumericDate(scope?.toDate);
    if (fromDate !== null) dates.push(fromDate);
    if (toDate !== null) dates.push(toDate);
    dates.push(...normalizeDates(scope?.requiredDates || []));
    if (fromDate !== null && toDate !== null) {
      dates.push(...generateMonthStartDates(Math.min(fromDate, toDate), Math.max(fromDate, toDate)));
    }
  });
  return normalizeDates(dates);
}

export function collectScopeCandidatesFromTransaction(transaction = {}) {
  const category = transaction?.category;
  const providerAssetId = transaction?.providerAssetId || null;
  const currency = transaction?.assetCurrency || transaction?.accountCurrency || transaction?.transactionCurrency || transaction?.currency;
  const scopeCandidates = [];

  if (transaction?.assetId) {
    scopeCandidates.push({
      assetId: transaction.assetId,
      providerAssetId,
      currency,
      category,
    });
  }

  ['transactionCurrency', 'accountCurrency', 'assetCurrency', 'currency'].forEach((currencyField) => {
    const currencyCode = transaction?.[currencyField];
    if (!currencyCode) return;
    scopeCandidates.push({
      assetId: currencyCode,
      providerAssetId: null,
      currency: currencyCode,
      category,
    });
  });

  return dedupeAssets(scopeCandidates);
}

export function extractAssetsFromTransactions(transactions = []) {
  return dedupeAssets(transactions.flatMap((transaction) => collectScopeCandidatesFromTransaction(transaction)));
}

export function extractAssetDateBoundsFromTransactions(transactions = []) {
  const bounds = {};

  (transactions || []).forEach((transaction) => {
    const numericDate = toNumericDate(transaction?.date);
    if (numericDate === null) return;

    collectScopeCandidatesFromTransaction(transaction).forEach((asset) => {
      const key = assetScopeKey(asset);
      if (!bounds[key]) {
        bounds[key] = { minDate: numericDate, maxDate: numericDate };
        return;
      }
      if (numericDate < bounds[key].minDate) bounds[key].minDate = numericDate;
      if (numericDate > bounds[key].maxDate) bounds[key].maxDate = numericDate;
    });
  });

  return bounds;
}

export function collectDataTransactions(state) {
  const categories = Object.values(state?.data || {});
  return categories.flatMap((categoryData) => (categoryData?.transactions || []).concat(categoryData?.simulatedTransactions || []));
}

export function buildScopesFromAssets({ assets = [], requiredDates = [], assetDateBounds = {} }) {
  const normalizedAssets = dedupeAssets(assets);
  const normalizedRequiredDates = normalizeDates(requiredDates);
  const fallbackDate = normalizedRequiredDates.length > 0 ? Math.max(...normalizedRequiredDates) : dayjs.utc().startOf('month').startOf('day').valueOf();
  const upperBoundDate = normalizedRequiredDates.length > 0 ? Math.max(...normalizedRequiredDates) : fallbackDate;

  const scopes = normalizedAssets.map((asset) => {
    const normalizedAsset = toApiAsset(asset);
    const key = assetScopeKey(normalizedAsset);
    const bounds = assetDateBounds[key];
    const fromDate = bounds?.minDate ?? fallbackDate;
    const toDate = Math.max(bounds?.maxDate ?? fromDate, upperBoundDate);

    return {
      asset: normalizedAsset,
      fromDate,
      toDate,
      requiredDates: normalizedRequiredDates.length > 0 ? normalizedRequiredDates : [toDate],
      includeManualQuotes: true,
    };
  });

  return normalizeScopes(scopes);
}

export function buildScopesFromTransactions({ transactions = [], requiredDates = [], assets = null }) {
  const effectiveAssets = Array.isArray(assets) ? assets : extractAssetsFromTransactions(transactions);
  const assetDateBounds = extractAssetDateBoundsFromTransactions(transactions);
  return buildScopesFromAssets({
    assets: effectiveAssets,
    requiredDates,
    assetDateBounds,
  });
}
